<template>
    <div class="request-list">
        <Table :columns="tblColumns" :data="requestsDataList" :loading="isLoading" class="request-list__table" />

        <div class="request-list__cards">
            <Spin v-if="isLoading" fix />
            <p v-if="!requestsDataList.length" class="request-list__empty">No data</p>
            <div v-for="row in requestsDataList" :key="row._id" class="request-card">
                <div class="request-card__head">
                    <div>
                        <div v-if="isAdmin" class="request-card__dates">{{ row.user && row.user.name }}</div>
                        <div class="request-card__meta">{{ formatCreditType(row.type) }}</div>
                        <div class="request-card__meta">
                            Credit Requested {{ row.credit_requested }} · Approved Credit {{ row.credit_approved }}
                        </div>
                    </div>
                    <span :class="['request-status', `request-status--${row.status}`]">
                        <span class="request-status__dot" />{{ formatStatus(row.status) }}
                    </span>
                </div>
                <div class="request-card__foot">
                    <span class="request-card__filed">Created {{ formatDate(row.createdAt) }}</span>
                    <div class="request-actions">
                        <Button
                            v-for="btn in actionsFor(row)"
                            :key="btn.content"
                            size="small"
                            :icon="btn.icon"
                            :class="{ 'request-actions__cancel': btn.danger, 'request-actions__approve': btn.success }"
                            @click="btn.onClick"
                        >
                            {{ btn.content }}
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <Page
            :total="totalCount"
            :current="currentPage"
            :show-total="true"
            :page-size="limit"
            size="small"
            class="request-list__pagination"
            @on-change="handleOnpageChanged"
        />
        <Create ref="approveCreditModal" :is-admin="isAdmin" @create="handleReloadList" />
        <ViewRequestModal ref="ViewRequest" />
        <UpdateRequestModal ref="UpdateRequest" @create="handleReloadList" />
        <DeleteRequestModal ref="DeleteRequest" @create="handleReloadList" />
    </div>
</template>

<script>
import moment from 'moment';
import {
    formatCreditType,
    formatStatus,
    renderActions,
    renderStatus,
} from '@/components/Requests/Dev/shared/tableRender';
import ViewRequestModal from '@/components/Requests/Dev/Leave/LeaveCredit/ViewRequestModal';
import UpdateRequestModal from '@/components/Requests/Dev/Leave/LeaveCredit/UpdateRequestModal';
import DeleteRequestModal from '@/components/Requests/Dev/Leave/LeaveCredit/DeleteRequestModal';
import { adminProcessLeaveCreditForApproval } from '@/components/Requests/Dev/Leave/LeaveCredit/services/credit.services';
// import ApproveCreditModal from '@/components/Requests/Admin/Leave/LeaveCredit/ApproveCreditModal.vue';
import Create from '@/components/Requests/Dev/Leave/LeaveCredit/create.vue';
export default {
    components: {
        Create,
        // ApproveCreditModal,
        ViewRequestModal,
        UpdateRequestModal,
        DeleteRequestModal,
    },
    props: {
        isAdmin: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        requestsDataList: {
            type: Array,
            default: () => [],
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
        totalPages: {
            type: Number,
            default: 0,
        },
        limit: {
            type: Number,
            default: 1,
        },
        totalPerPage: {
            type: Object,
            default: () => {},
        },
    },

    data() {
        return {
            currentCredit: {
                id: null,
                status: null,
                approved_credit: 0,
            },
            tblColumns: [
                {
                    title: 'Date Created',
                    key: 'createdAt',
                    minWidth: 120,
                    render: (h, params) => {
                        return h(
                            'div',
                            params.row.createdAt ? moment(params.row.createdAt).utc().format('YYYY-MM-DD') : ''
                        );
                    },
                },
                ...(this.isAdmin
                    ? [
                          {
                              title: 'Name',
                              minWidth: 180,
                              render: (h, params) => {
                                  return h('div', params.row.user.name);
                              },
                          },
                      ]
                    : []),
                {
                    title: 'type',
                    key: 'type',
                    minWidth: 140,
                    render: (h, params) => {
                        return h('div', formatCreditType(params.row.type));
                    },
                },
                {
                    title: 'Credit Requested',
                    key: 'credit_requested',
                    minWidth: 140,
                    render: (h, params) => {
                        return h('div', params.row.credit_requested);
                    },
                },
                {
                    title: 'Approved Credit',
                    key: 'credit_approved',
                    minWidth: 140,
                    render: (h, params) => {
                        return h('div', params.row.credit_approved);
                    },
                },
                {
                    title: 'Status',
                    key: 'status',
                    minWidth: 120,
                    render: (h, params) => renderStatus(h, params.row.status),
                },
                {
                    title: 'Actions',
                    width: 160,
                    align: 'left',
                    render: (h, params) => renderActions(h, this.actionsFor(params.row)),
                },
            ],
            tblData: [],
        };
    },
    methods: {
        formatStatus,
        formatCreditType,
        formatDate(date) {
            return date ? moment(date).utc().format('YYYY-MM-DD') : '';
        },
        // Same buttons and visibility rules as before, just restyled
        actionsFor(row) {
            const isPending = row.status && row.status.includes('pending');
            return [
                {
                    content: 'View',
                    icon: 'md-eye',
                    onClick: () => this.showConfirmationProcessRequest({ details: row, isAdmin: this.isAdmin }),
                },
                ...(!this.isAdmin && isPending
                    ? [
                          {
                              content: 'Update',
                              icon: 'md-create',
                              onClick: () => this.showUpdateRequest({ details: row }),
                          },
                      ]
                    : []),
                ...(this.isAdmin && isPending
                    ? [
                          {
                              content: 'Approve',
                              icon: 'md-checkmark',
                              success: true,
                              onClick: () =>
                                  this.handleProcessRequest({
                                      id: row._id,
                                      credit_approved: row.credit_requested,
                                      status: 'approved',
                                  }),
                          },
                          {
                              content: 'Reject',
                              icon: 'md-close',
                              danger: true,
                              onClick: () =>
                                  this.handleProcessRequest({ id: row._id, status: 'rejected', credit_approved: 0 }),
                          },
                      ]
                    : []),
                ...(!this.isAdmin && isPending
                    ? [
                          {
                              content: 'Cancel',
                              icon: 'ios-remove-circle-outline',
                              danger: true,
                              onClick: () => this.$refs.DeleteRequest.openModal(row, 'cancel'),
                          },
                      ]
                    : []),
            ];
        },
        async handleProcessRequest({ id, status, credit_approved }) {
            try {
                await adminProcessLeaveCreditForApproval({ id, status, credit_approved });
                this.$Message.success({ content: `Leave credit ${status}`, duration: 5 });
                this.handleReloadList();
            } catch (err) {
                this.$Message.error({ content: 'err.message', duration: 3 });
            }
        },
        async showConfirmationProcessRequest({ details, isAdmin = false }) {
            this.currentCredit = details;
            if (isAdmin) this.$refs.approveCreditModal.openModalForAdminProcessingLeaves(details);
            else {
                this.$refs.approveCreditModal.openModalForReadOnly(this.currentCredit);
            }
        },
        async showUpdateRequest({ details }) {
            this.$refs.approveCreditModal.openModalForEditing(details);
        },
        handleReloadList() {
            this.$emit('reload');
        },
        handleOnpageChanged(page) {
            this.$emit('on-page-change', page);
        },
    },
};
</script>

<style lang="scss">
ul.pagination.ivu-page {
    text-align: end;
}
.requestsList button {
    width: 25px;
    margin-left: 10px;
}
.requestsList i.ivu-icon {
    margin-left: -12.5px;
}
.btn-disable {
    cursor: not-allowed;
    pointer-events: none;

    /*Button disabled - CSS color class*/
    color: #c0c0c0;
    background-color: #ffffff;
}

.btn-hide {
    display: none;
}
.statusLabel {
    display: inline-block;
    color: #fff;
    border-radius: 5px;
    box-shadow: 0 0 5px -1px rgba(0, 0, 0, 0.2);
    vertical-align: middle;
    max-width: 250px;
    padding: 5px;
    text-align: center;
}
.success {
    background: #49e97e;
    font-weight: bold;
}
.warning {
    background: #ffc107;
    font-weight: bold;
}
.danger {
    background: #e9495d;
    font-weight: bold;
}
.primary {
    background: #211841;
}
.secondary {
    background: #828899;
}
</style>

<style lang="scss" scoped>
@import '../../shared/request-table.scss';
</style>
